import { createSelector } from 'reselect';
import { denormalize } from 'normalizr';
import { CALL_API, HTTP_METHODS } from '../middlewares/api.js';
import { changeStrategy as changeStrategySchema } from '../lib/schema.js';
import { selectEntities } from './entities.js';

// API Actions

const RETRIEVE_CHANGE_STRATEGY_REQUEST =
  'changesync/change-strategy/RETRIEVE_CHANGE_STRATEGY_REQUEST';
const RETRIEVE_CHANGE_STRATEGY_SUCCESS =
  'changesync/change-strategy/RETRIEVE_CHANGE_STRATEGY_SUCCESS';
const RETRIEVE_CHANGE_STRATEGY_FAILURE =
  'changesync/change-strategy/RETRIEVE_CHANGE_STRATEGY_FAILURE';

const UPDATE_CHANGE_STRATEGY_FIELDS_REQUEST =
  'changesync/change-strategy/UPDATE_CHANGE_STRATEGY_FIELDS_REQUEST';
const UPDATE_CHANGE_STRATEGY_FIELDS_SUCCESS =
  'changesync/change-strategy/UPDATE_CHANGE_STRATEGY_FIELDS_SUCCESS';
const UPDATE_CHANGE_STRATEGY_FIELDS_FAILURE =
  'changesync/change-strategy/UPDATE_CHANGE_STRATEGY_FIELDS_FAILURE';

const CREATE_UPDATE_CHANGE_STRATEGY_DRAFT_REQUEST =
  'changesync/change-strategy/CREATE_UPDATE_CHANGE_STRATEGY_DRAFT_REQUEST';
const CREATE_UPDATE_CHANGE_STRATEGY_DRAFT_SUCCESS =
  'changesync/change-strategy/CREATE_UPDATE_CHANGE_STRATEGY_DRAFT_SUCCESS';
const CREATE_UPDATE_CHANGE_STRATEGY_DRAFT_FAILURE =
  'changesync/change-strategy/CREATE_UPDATE_CHANGE_STRATEGY_DRAFT_FAILURE';

const FINISH_UPDATE_CHANGE_STRATEGY_DRAFT_REQUEST =
  'changesync/change-strategy/FINISH_UPDATE_CHANGE_STRATEGY_DRAFT_REQUEST';
const FINISH_UPDATE_CHANGE_STRATEGY_DRAFT_SUCCESS =
  'changesync/change-strategy/FINISH_UPDATE_CHANGE_STRATEGY_DRAFT_SUCCESS';
const FINISH_UPDATE_CHANGE_STRATEGY_DRAFT_FAILURE =
  'changesync/change-strategy/FINISH_UPDATE_CHANGE_STRATEGY_DRAFT_FAILURE';
const RESET_DRAFT_ID = 'changesync/change-strategy/RESET_DRAFT_ID';

// Initial State

const initialState = {
  retrieveChangeStrategy: {
    changeStrategyId: null,
    pending: false,
    success: false,
    error: null,
  },
  changeStrategyDraftId: null,
  query: '',
};

// Reducer

export default (state = initialState, action) => {
  switch (action.type) {
    case RETRIEVE_CHANGE_STRATEGY_REQUEST:
      return {
        ...state,
        retrieveChangeStrategy: {
          ...state.retrieveChangeStrategy,
          pending: true,
          success: false,
          error: null,
        },
      };

    case RETRIEVE_CHANGE_STRATEGY_SUCCESS:
      return {
        ...state,
        retrieveChangeStrategy: {
          ...state.retrieveChangeStrategy,
          changeStrategyId:
            action.payload !== 'No Content' ? action.payload.result : null,
          pending: false,
          success: true,
        },
      };

    case CREATE_UPDATE_CHANGE_STRATEGY_DRAFT_SUCCESS:
      return {
        ...state,
        changeStrategyDraftId: action.payload.result,
      };

    case FINISH_UPDATE_CHANGE_STRATEGY_DRAFT_SUCCESS:
      return {
        ...state,
        changeStrategyDraftId: null,
      };

    case RESET_DRAFT_ID:
      return {
        ...state,
        changeStrategyDraftId: null,
      };

    default:
      return state;
  }
};

// Action Creators

export const updateChangeStrategyCustomFields = (id, strategyFields) => ({
  [CALL_API]: {
    types: [
      UPDATE_CHANGE_STRATEGY_FIELDS_REQUEST,
      UPDATE_CHANGE_STRATEGY_FIELDS_SUCCESS,
      UPDATE_CHANGE_STRATEGY_FIELDS_FAILURE,
    ],
    endpoint: `projects/${id}/update_change_strategy_fields`,
    method: HTTP_METHODS.PUT,
    // schema: projectSchema,
    body: strategyFields,
  },
});

export const retrieveChangeStrategy = (projectId) => (dispatch) => {
  return dispatch({
    [CALL_API]: {
      types: [
        RETRIEVE_CHANGE_STRATEGY_REQUEST,
        RETRIEVE_CHANGE_STRATEGY_SUCCESS,
        RETRIEVE_CHANGE_STRATEGY_FAILURE,
      ],
      endpoint: `projects/${projectId}/change_strategy`,
      method: HTTP_METHODS.GET,
      schema: changeStrategySchema,
      disabledCamelizeKeys: true,
    },
    meta: {
      projectId,
    },
  });
};

export const updateChangeStrategyDraft = (
  projectId,
  changeStrategy,
  isDisabledDecamelizeKeys = false,
  isDisabledCamelizeKeys = false,
  isDeleteDraft = false
) => {
  return {
    [CALL_API]: {
      types: [
        CREATE_UPDATE_CHANGE_STRATEGY_DRAFT_REQUEST,
        CREATE_UPDATE_CHANGE_STRATEGY_DRAFT_SUCCESS,
        CREATE_UPDATE_CHANGE_STRATEGY_DRAFT_FAILURE,
      ],
      endpoint: `projects/${projectId}/change_strategy`,
      method: HTTP_METHODS.PUT,
      disabledDecamelizeKeys: isDisabledDecamelizeKeys,
      disabledCamelizeKeys: isDisabledCamelizeKeys,
      schema: changeStrategySchema,
      body: changeStrategy,
      query: isDeleteDraft
        ? {
            delete_draft: isDeleteDraft,
          }
        : {},
    },
  };
};

export const deleteChangeStrategyDraft = (projectId) => {
  return {
    [CALL_API]: {
      types: [
        FINISH_UPDATE_CHANGE_STRATEGY_DRAFT_REQUEST,
        FINISH_UPDATE_CHANGE_STRATEGY_DRAFT_SUCCESS,
        FINISH_UPDATE_CHANGE_STRATEGY_DRAFT_FAILURE,
      ],
      endpoint: `projects/${projectId}/change_strategy`,
      method: HTTP_METHODS.DELETE,
    },
  };
};

export const updateChangeStrategy = (
  projectId,
  changeStrategy,
  isDisabledDecamelizeKeys = false,
  isDisabledCamelizeKeys = false
) => {
  return {
    [CALL_API]: {
      types: [
        FINISH_UPDATE_CHANGE_STRATEGY_DRAFT_REQUEST,
        FINISH_UPDATE_CHANGE_STRATEGY_DRAFT_SUCCESS,
        FINISH_UPDATE_CHANGE_STRATEGY_DRAFT_FAILURE,
      ],
      endpoint: `projects/${projectId}/change_strategy`,
      method: HTTP_METHODS.PUT,
      disabledDecamelizeKeys: isDisabledDecamelizeKeys,
      disabledCamelizeKeys: isDisabledCamelizeKeys,
      body: changeStrategy,
      query: {
        finish: true,
      },
    },
  };
};

export const resetChangeStrategyDraftId = () => ({
  type: RESET_DRAFT_ID,
});

export const selectRetrieveChangeStrategyState = (state) =>
  state.changeStrategy.retrieveChangeStrategy;

const selectChangeStrategyId = createSelector(
  selectRetrieveChangeStrategyState,
  (state) => state.changeStrategyId
);

const selectChangeStrategyDraftId = (state) =>
  state.changeStrategy.changeStrategyDraftId;

export const selectChangeStrategy = createSelector(
  selectChangeStrategyId,
  selectEntities,
  (changeStrategyId, entities) =>
    denormalize(changeStrategyId, changeStrategySchema, entities)
);

export const selectDraftChangeStrategy = createSelector(
  selectChangeStrategyDraftId,
  selectEntities,
  (changeStrategyId, entities) =>
    denormalize(changeStrategyId, changeStrategySchema, entities)
);
